"use client";

import React from "react";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { customSanitize } from "@/utils/customSanitize";
import styles from "./TextCard.module.scss";
import { ContentTag } from "../ContentTag/ContentTag";

export type TextCardElement = {
  title: string;
  subline: string;
  price: string;
  description: string;
  location: string;
};

interface TextCardProps {
  element: TextCardElement;
  headingLevel: number;
  skeleton?: boolean;
}

const InfoLine = ({
  price,
  skeletonElementClass,
}: {
  price: string;
  skeletonElementClass: string;
}) => {
  if (!price) {
    return null;
  }

  return (
    <div className={`${styles["info-line"]} ${skeletonElementClass} p-0`}>
      <ContentTag content={price} />
    </div>
  );
};

export const TextCard = ({
  element,
  headingLevel,
  skeleton = false,
}: TextCardProps) => {
  const { getLabel } = useGlobalContext();
  const skeletonElementClass = skeleton ? "skeleton-element" : "";
  const hasIntro = Boolean(element.title || element.subline);
  const hasDescription = Boolean(element.description);
  const hasLocation = Boolean(element.location);

  return (
    <article
      className={`${styles["text-card"]} is-box`}
      aria-hidden={skeleton || undefined}
    >
      {hasIntro && (
        <header className={styles["header"]}>
          {element.title && (
            <p
              role="heading"
              aria-level={headingLevel}
              className={`${styles["title"]} ${skeletonElementClass} pb-0`}
              dangerouslySetInnerHTML={{
                __html: customSanitize(element.title),
              }}
            />
          )}
          {element.subline && (
            <p
              className={`${styles["subline"]} ${skeletonElementClass} pb-0`}
              dangerouslySetInnerHTML={{
                __html: customSanitize(element.subline),
              }}
            />
          )}
        </header>
      )}
      <div className={`${styles["divider"]}`} aria-hidden={true} />
      <div className={styles["content"]}>
        <InfoLine
          price={element.price}
          skeletonElementClass={skeletonElementClass}
        />
        {hasDescription && (
          <div
            className={`${styles["description"]} ${skeletonElementClass}`}
            dangerouslySetInnerHTML={{
              __html: customSanitize(element.description),
            }}
          />
        )}
        {hasLocation && (
          <p className={`${styles["location"]} ${skeletonElementClass} pb-0`}>
            <strong>{getLabel("location", "Ort")}: </strong>
            <span
              dangerouslySetInnerHTML={{
                __html: customSanitize(element.location),
              }}
            />
          </p>
        )}
      </div>
    </article>
  );
};
